export type Locale = 'es' | 'en';

export type Availability = 'OPEN_TO_WORK' | 'FREELANCE' | 'NOT_LOOKING';

export type TechCategory =
  | 'LANGUAGE'
  | 'FRONTEND'
  | 'BACKEND'
  | 'DATABASE'
  | 'INFRASTRUCTURE'
  | 'TOOL'
  | 'PRACTICE';

export type ProjectStatus = 'IN_PRODUCTION' | 'IN_PROGRESS' | 'COMPLETED' | 'ARCHIVED';

export type ProjectScale = 'MAJOR' | 'MINOR';

export type ExperienceKind = 'WORK' | 'EDUCATION';

export interface Profile {
  id: string;
  fullName: string;
  headlineEs: string;
  headlineEn: string;
  summaryEs: string;
  summaryEn: string;
  bioEs: string;
  bioEn: string;
  photoUrl: string | null;
  resumeEsUrl: string | null;
  resumeEnUrl: string | null;
  email: string;
  phone: string | null;
  locationEs: string;
  locationEn: string;
  availability: Availability;
  startedCodingYear: number;
}

export interface SocialLink {
  id: string;
  platform: string;
  label: string;
  url: string;
  iconKey: string;
  order: number;
  isVisible: boolean;
}

export interface Technology {
  id: string;
  name: string;
  slug: string;
  category: TechCategory;
  level: number;
  firstUsedYear: number | null;
  iconKey: string | null;
  color: string | null;
  isFeatured: boolean;
  order: number;
  projectCount?: number;
}

export interface SkillGroup {
  category: TechCategory;
  items: Technology[];
}

export interface ProjectMetric {
  es: string;
  en: string;
  value: string | number;
}

export interface ProjectComponent {
  name: string;
  es: string;
  en: string;
  tagEs?: string;
  tagEn?: string;
}

export interface ProjectImage {
  id: string;
  url: string;
  altEs: string;
  altEn: string;
  order: number;
}

export interface Project {
  id: string;
  slug: string;
  title: string;
  summaryEs: string;
  summaryEn: string;
  descriptionEs: string;
  descriptionEn: string;
  roleEs: string;
  roleEn: string;
  highlightsEs: string[];
  highlightsEn: string[];
  metrics: ProjectMetric[] | null;
  components: ProjectComponent[] | null;
  coverImage: string | null;
  repoUrl: string | null;
  liveUrl: string | null;
  isRepoPrivate: boolean;
  status: ProjectStatus;
  scale: ProjectScale;
  startDate: string;
  endDate: string | null;
  isFeatured: boolean;
  isPublished: boolean;
  order: number;
  technologies: Technology[];
  images: ProjectImage[];
}

export interface Experience {
  id: string;
  kind: ExperienceKind;
  organization: string;
  titleEs: string;
  titleEn: string;
  descriptionEs: string;
  descriptionEn: string;
  locationEs: string | null;
  locationEn: string | null;
  startDate: string;
  endDate: string | null;
  isCurrent: boolean;
  logoUrl: string | null;
  url: string | null;
  order: number;
  isVisible: boolean;
}

export interface Certificate {
  id: string;
  titleEs: string;
  titleEn: string;
  issuer: string;
  descriptionEs: string | null;
  descriptionEn: string | null;
  issueDate: string;
  expiryDate: string | null;
  credentialId: string | null;
  credentialUrl: string | null;
  imageUrl: string | null;
  order: number;
  isVisible: boolean;
}

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  isRead: boolean;
  isArchived: boolean;
  createdAt: string;
}

export interface MediaAsset {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  width: number | null;
  height: number | null;
  url: string;
  altEs: string | null;
  altEn: string | null;
  createdAt: string;
}

export interface SiteSetting {
  key: string;
  value: string;
  isPublic: boolean;
}

export interface Bootstrap {
  profile: Profile;
  socialLinks: SocialLink[];
  projects: Project[];
  skills: SkillGroup[];
  experience: { work: Experience[]; education: Experience[] };
  certificates: Certificate[];
  settings: SiteSetting[];
}

export interface AdminUser {
  adminId: string;
  username: string;
  role: string;
}

export interface AnalyticsSummary {
  days: number;
  total: number;
  topPaths: { path: string; views: number }[];
  timeline: { day: string; views: number }[];
}

export interface DashboardData {
  counts: {
    projects: number;
    publishedProjects: number;
    technologies: number;
    certificates: number;
    unreadMessages: number;
  };
  recentMessages: ContactMessage[];
  analytics: AnalyticsSummary;
}

/**
 * Picks the field matching the active locale from a `<field>Es` / `<field>En`
 * pair, so components never branch on language themselves. The constraint ties
 * `field` to the object, so a typo like `'headlne'` fails to compile.
 */
export function localized<
  K extends string,
  T extends Partial<Record<`${K}Es` | `${K}En`, string | null>>,
>(source: T, field: K, locale: Locale): string {
  const key = `${field}${locale === 'es' ? 'Es' : 'En'}` as keyof T;
  return (source[key] as string | null | undefined) ?? '';
}
